import { useEffect, useId, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isVisible(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
}

export function getFocusableElements(root: HTMLElement) {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
}

export function isTopmostOverlay(root: HTMLElement) {
  const overlays = Array.from(document.querySelectorAll<HTMLElement>('[data-cap4-overlay-root="true"]'));
  return overlays[overlays.length - 1] === root;
}

export interface OverlayFocusOptions {
  active?: boolean;
  initialFocus?: string | HTMLElement | null;
  /** Stable focus target used when the exact opener is removed while the overlay is open. */
  restoreFocus?: string | HTMLElement | null;
  closeOnEscape?: boolean;
  onEscape?: () => void;
}

/** Shared focus lifecycle for modal, drawer and sheet overlays. */
export function useOverlayFocus<T extends HTMLElement>({
  active = true,
  initialFocus = null,
  restoreFocus = null,
  closeOnEscape = true,
  onEscape,
}: OverlayFocusOptions = {}) {
  const rootRef = useRef<T | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const openerSelectorRef = useRef<string | null>(null);
  const initialFocusRef = useRef(initialFocus);
  const restoreFocusRef = useRef(restoreFocus);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const onEscapeRef = useRef(onEscape);
  const overlayId = useId();

  initialFocusRef.current = initialFocus;
  restoreFocusRef.current = restoreFocus;
  closeOnEscapeRef.current = closeOnEscape;
  onEscapeRef.current = onEscape;

  // Capture during the opening render. The trigger may be removed by the same
  // commit that mounts the overlay (for example a mobile bottom navigation),
  // so waiting for an effect can otherwise lose the exact opener.
  if (active && !openerRef.current && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
    openerRef.current = document.activeElement;
    const openerId = document.activeElement.id;
    const openerLabel = document.activeElement.getAttribute('aria-label');
    openerSelectorRef.current = openerId
      ? `#${CSS.escape(openerId)}`
      : openerLabel
        ? `[aria-label="${CSS.escape(openerLabel)}"]`
        : null;
  }

  useEffect(() => {
    if (!active) return;
    const root = rootRef.current;
    if (!root) return;

    root.dataset.cap4OverlayRoot = 'true';
    root.dataset.cap4OverlayId = overlayId;
    if (!openerRef.current && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      openerRef.current = document.activeElement;
      const openerId = document.activeElement.id;
      const openerLabel = document.activeElement.getAttribute('aria-label');
      openerSelectorRef.current = openerId
        ? `#${CSS.escape(openerId)}`
        : openerLabel
          ? `[aria-label="${CSS.escape(openerLabel)}"]`
          : null;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      const requestedFocus = initialFocusRef.current;
      const preferred = typeof requestedFocus === 'string'
        ? root.querySelector<HTMLElement>(requestedFocus)
        : requestedFocus;
      (preferred ?? getFocusableElements(root)[0] ?? root).focus({ preventScroll: true });
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmostOverlay(root)) return;
      if (event.key === 'Escape' && closeOnEscapeRef.current && onEscapeRef.current) {
        event.preventDefault();
        event.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(root);
      if (focusable.length === 0) {
        event.preventDefault();
        root.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown, true);
      delete root.dataset.cap4OverlayRoot;
      delete root.dataset.cap4OverlayId;
      const opener = openerRef.current;
      const openerSelector = openerSelectorRef.current;
      const requestedRestore = restoreFocusRef.current;
      openerRef.current = null;
      openerSelectorRef.current = null;
      const requestedTarget = typeof requestedRestore === 'string'
        ? document.querySelector<HTMLElement>(requestedRestore)
        : requestedRestore;
      const focusTarget = requestedTarget && document.contains(requestedTarget)
        ? requestedTarget
        : opener && document.contains(opener)
          ? opener
          : openerSelector
            ? document.querySelector<HTMLElement>(openerSelector)
            : null;

      // React has committed the close before passive cleanup, so focus a
      // mounted/remounted opener now instead of exposing a BODY-focus frame.
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      } else if (typeof requestedRestore === 'string' || openerSelector) {
        window.requestAnimationFrame(() => {
          const fallbackTarget = typeof requestedRestore === 'string'
            ? document.querySelector<HTMLElement>(requestedRestore)
            : openerSelector
              ? document.querySelector<HTMLElement>(openerSelector)
              : null;
          fallbackTarget?.focus({ preventScroll: true });
        });
      }
    };
  }, [active, overlayId]);

  return rootRef;
}
